import { useCallback, useEffect, useState } from "react";

/**
 * Takvim / saat seçici ikonlarının arka planını uygulama genelinde yöneten
 * tema ayarı. Değer <html data-picker-icons="..."> olarak yazılır ve
 * index.css içindeki kurallar buna göre kontrast üretir.
 *
 *  - auto  : arka plan parlaklığına göre otomatik uyarlanır (varsayılan)
 *  - soft  : hafif yumuşak rozet arka planı
 *  - solid : tam dolu, en yüksek kontrastlı rozet
 *  - none  : arka plan yok (yalnızca ikon tonlaması)
 */
export type PickerIconTheme = "auto" | "soft" | "solid" | "none";

export const PICKER_ICON_THEMES: PickerIconTheme[] = ["auto", "soft", "solid", "none"];

const LS_KEY = "picker-icon-theme";

const isValid = (v: unknown): v is PickerIconTheme =>
  typeof v === "string" && (PICKER_ICON_THEMES as string[]).includes(v);

export function readPickerIconTheme(): PickerIconTheme {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (isValid(v)) return v;
  } catch {
    /* storage unavailable */
  }
  return "auto";
}

export function applyPickerIconTheme(theme: PickerIconTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-picker-icons", theme);
}

/** Uygulama açılışında (main.tsx) çağrılır. */
export function initPickerIconTheme() {
  applyPickerIconTheme(readPickerIconTheme());
}

export function usePickerIconTheme() {
  const [theme, setThemeState] = useState<PickerIconTheme>(() => readPickerIconTheme());

  useEffect(() => {
    applyPickerIconTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: PickerIconTheme) => {
    setThemeState(next);
    try {
      localStorage.setItem(LS_KEY, next);
    } catch {
      /* storage unavailable */
    }
    applyPickerIconTheme(next);
    window.dispatchEvent(new CustomEvent("picker-icon-theme:changed", { detail: { theme: next } }));
  }, []);

  // Diğer sekmelerle senkron kal
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && isValid(e.newValue)) setThemeState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return { theme, setTheme };
}

export default usePickerIconTheme;
